/**
 * Geode Voice (UPT-2000 lot V0 §5.4) — exécution des quatre outils du modèle
 * Adapté de JARVIS Local (licence MIT, © 2026 Thomas Berton) — voir THIRD_PARTY_NOTICE.md.
 * vocal : `ask_copilot`, `show_card`, `show_report`, `cancel`. Tous sont
 * exécutés PAR LE NAVIGATEUR avec le jeton de l'utilisateur : aucune écriture,
 * aucun outil serveur, aucun accès fichier.
 * UPT-2124 J2a (spec `web/SPEC-UPT-2124-J2.md` §2 n° 6) : tout autre nom est cherché dans le registre des outils enregistrés par une page
 * (`voiceToolRegistry`, ex. `next_step` de la fiche d'un dossier) AVANT de répondre `unknown_tool`.
 */
import { askCopilotOnce, WRITE_CONFIRMATION_REFUSAL } from './askCopilot';
import { useVoiceStore } from './voiceStore';
import { executeRegisteredVoiceTool } from './voiceToolRegistry';
import type { VoiceReport, VoiceToolCall } from './voiceTypes';

export type VoiceToolContext = {
  /** Contexte de page courant (même forme que le tiroir du copilote). */
  pageContext: string;
  /** Interrompt la réponse en cours (session réelle : événement d'annulation). */
  onCancel?: () => void;
  /** Signal d'abandon de l'appel copilote en cours. */
  abortSignal?: AbortSignal;
};

export type VoiceToolResult = {
  /** Sortie JSON renvoyée au modèle (function_call_output). */
  outputJson: string;
};

const parseArguments = (argumentsJson: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(argumentsJson || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Exécute un appel d'outil du modèle vocal ; renvoie la sortie JSON à renvoyer. */
export const executeVoiceTool = async (
  call: VoiceToolCall,
  context: VoiceToolContext
): Promise<VoiceToolResult> => {
  const store = useVoiceStore.getState();
  const args = parseArguments(call.argumentsJson);

  switch (call.name) {
    case 'ask_copilot': {
      const question = asString(args.question);
      if (!question.trim()) {
        return { outputJson: JSON.stringify({ error: 'missing_question' }) };
      }
      try {
        const result = await askCopilotOnce(question, context.pageContext, context.abortSignal);
        return { outputJson: JSON.stringify({ answer: result.text }) };
      } catch {
        return { outputJson: JSON.stringify({ error: 'copilot_unavailable' }) };
      }
    }
    case 'show_card': {
      store.addCard({ title: asString(args.title), bodyMd: asString(args.bodyMd) });
      return { outputJson: JSON.stringify({ shown: 'card' }) };
    }
    case 'show_report': {
      const kpis = Array.isArray(args.kpis)
        ? (args.kpis as Record<string, unknown>[]).map((kpi) => ({
            label: asString(kpi?.label),
            value: typeof kpi?.value === 'number' ? String(kpi.value) : asString(kpi?.value),
          }))
        : undefined;
      const tableValue = args.table as { columns?: unknown; rows?: unknown } | undefined;
      const table: VoiceReport['table'] =
        tableValue && Array.isArray(tableValue.columns) && Array.isArray(tableValue.rows)
          ? {
              columns: (tableValue.columns as unknown[]).map(asString),
              rows: (tableValue.rows as unknown[]).map((row) =>
                Array.isArray(row) ? (row as unknown[]).map(asString) : []
              ),
            }
          : undefined;
      store.addReport({ title: asString(args.title), kpis, table });
      return { outputJson: JSON.stringify({ shown: 'report' }) };
    }
    case 'cancel': {
      context.onCancel?.();
      return { outputJson: JSON.stringify({ cancelled: true }) };
    }
    default: {
      // UPT-2124 J2a : repli sur le registre des outils enregistrés par une page (`next_step`…) AVANT `unknown_tool`.
      const registered = await executeRegisteredVoiceTool(call.name, args);
      if (registered) {
        return registered;
      }
      // Outil inconnu : journalisé en console de développement seulement.
      // eslint-disable-next-line no-console -- trace de développement exigée par la spec V0
      console.debug('voice: unknown_tool', call.name);
      return { outputJson: JSON.stringify({ error: 'unknown_tool' }) };
    }
  }
};

export { WRITE_CONFIRMATION_REFUSAL };
